import { Sparkles } from "lucide-react";
import { transposeAbc } from "../../lib/abcUtils";
import {
  skin,
  PURPLE,
  NotationPanel,
  ExportMock,
  AbcChartMock,
  FameMock,
  NameSearchMock,
  FlagsMock,
  PricingTableMock,
  InstallMock,
  PlayerMock,
  FaqMock,
  RarityMock,
  AboutCtaMock,
} from "./SocialMocks";
import { FitNotation, FitAbcText } from "./Fit";
import { ScreenSlide } from "./ScreenSlide";

export const INK = "#2d2824";
export const CREAM = "#fdfbf7";
export const BORDER = "#e5e0d8";

const StaffLines = ({ dark }) => (
  <svg width="100%" height="100%" style={{ position: "absolute", inset: 0, opacity: dark ? 0.07 : 0.1 }}>
    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((i) => (
      <g key={i}>
        {[0, 1, 2, 3, 4].map((l) => (
          <rect key={l} x="0" y={i * 170 + l * 14} width="100%" height="3" fill={dark ? CREAM : INK} />
        ))}
      </g>
    ))}
  </svg>
);

export const Frame = ({ w, h, dark, pad = 72, children, id }) => (
  <div
    id={id}
    data-social-card={id}
    style={{
      width: w,
      height: h,
      padding: pad,
      position: "relative",
      overflow: "hidden",
      background: dark ? INK : CREAM,
      color: dark ? CREAM : INK,
      fontFamily: "Nunito, sans-serif",
      display: "flex",
      flexDirection: "column",
    }}
  >
    <StaffLines dark={dark} />
    <div
      style={{
        position: "absolute",
        width: w * 1.2,
        height: w * 1.2,
        right: -w * 0.45,
        top: -w * 0.5,
        borderRadius: "50%",
        background: `radial-gradient(circle, ${PURPLE}${dark ? "55" : "22"} 0%, transparent 65%)`,
      }}
    />
    <div style={{ position: "relative", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      {children}
    </div>
  </div>
);

export const Wordmark = ({ badge = "full", size = 120, dark = true, center = false, stacked = false }) => (
  <div
    style={{
      display: "flex",
      flexDirection: stacked ? "column" : "row",
      alignItems: "center",
      textAlign: stacked ? "center" : "left",
      gap: size * (stacked ? 0.14 : 0.22),
      justifyContent: center ? "center" : "flex-start",
    }}
  >
    <img src="/foinn-logo.png" alt="Foinn.org" style={{ width: size * (stacked ? 1.5 : 1), height: size * (stacked ? 1.5 : 1), objectFit: "contain" }} />
    {badge !== "logo" && (
      <div style={{ lineHeight: 1 }}>
        <div
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: size * 0.62,
            fontWeight: 700,
            color: dark ? CREAM : INK,
          }}
        >
          Foinn.org
        </div>
        {badge === "full" && (
          <div
            style={{
              fontSize: size * 0.2,
              letterSpacing: size * 0.035,
              fontWeight: 800,
              textTransform: "uppercase",
              color: dark ? `${CREAM}aa` : "#6e655b",
              marginTop: size * 0.06,
            }}
          >
            Folk Tune Finder
          </div>
        )}
      </div>
    )}
  </div>
);

const Domain = ({ dark = true, size = 30 }) => (
  <span
    style={{
      alignSelf: "flex-start",
      border: `3px solid ${dark ? `${CREAM}55` : BORDER}`,
      color: dark ? CREAM : INK,
      borderRadius: 999,
      padding: `${size * 0.35}px ${size * 0.8}px`,
      fontSize: size,
      fontWeight: 900,
      letterSpacing: 1,
    }}
  >
    foinn.org
  </span>
);

// ------------------------------------------------------------------ cover pack
export const CoverCard = ({ w, h, badge, dark = true, text1, text2 }) => {
  const ratio = w / h;
  const banner = ratio > 2.2;
  const unit = Math.min(w, h);
  const headline = !!(text1?.trim() || text2?.trim());
  const accent = dark ? "#c89bff" : PURPLE;
  const Headline = ({ size, align = "left" }) => (
    <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: size, lineHeight: 1.03, fontWeight: 700, textAlign: align }}>
      {text1}
      {text1 && text2 && <br />}
      <span style={{ color: accent }}>{text2}</span>
    </div>
  );

  // no headline: the mark (+ name) takes the centre, like the logo pack
  if (!headline) {
    const text = badge !== "logo";
    const size = !text
      ? unit * (banner ? 0.62 : 0.5)
      : banner || ratio > 1.6
        ? Math.min(h * 0.42, (w * 0.78) / 3.9)
        : Math.min(unit * 0.3, (w * 0.8) / 2.9);
    return (
      <Frame w={w} h={h} dark={dark} pad={unit * 0.08} id={`cover-${w}x${h}`}>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Wordmark badge={badge} size={size} dark={dark} center stacked={text && ratio <= 1.6} />
        </div>
      </Frame>
    );
  }

  if (banner) {
    return (
      <Frame w={w} h={h} dark={dark} pad={unit * 0.12} id={`cover-${w}x${h}`}>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "space-between", gap: unit * 0.15 }}>
          <Wordmark badge={badge} size={unit * 0.3} dark={dark} />
          <div style={{ maxWidth: "52%" }}>
            <Headline size={unit * 0.13} align="right" />
          </div>
        </div>
      </Frame>
    );
  }

  return (
    <Frame w={w} h={h} dark={dark} pad={unit * 0.09} id={`cover-${w}x${h}`}>
      <Wordmark badge={badge} size={unit * (ratio > 1.6 ? 0.15 : 0.14)} dark={dark} center={ratio <= 1.6} />
      <div style={{ marginTop: "auto" }}>
        <Headline size={unit * (ratio > 1.6 ? 0.1 : 0.115)} />
      </div>
      <div
        style={{
          marginTop: unit * 0.05,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 24,
        }}
      >
        {badge !== "logo" && <Domain dark={dark} size={unit * 0.03} />}
        <span style={{ fontSize: unit * 0.028, fontWeight: 700, color: skin(dark).muted }}>Free · no advertising</span>
      </div>
    </Frame>
  );
};

// ------------------------------------------------------------------- tune post
const CardHeader = ({ right, dark }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
    <Wordmark badge="domain" size={64} dark={dark} />
    {right && <span style={{ fontSize: 28, fontWeight: 900, color: PURPLE, letterSpacing: 2 }}>{right}</span>}
  </div>
);

export const TuneTitleCard = ({ w, h, tune, version, fame, dark = true }) => (
  <Frame w={w} h={h} dark={dark} id="tune-title">
    <Wordmark badge="full" size={92} dark={dark} />
    <div style={{ marginTop: "auto" }}>
      <div
        style={{
          fontSize: 34,
          fontWeight: 900,
          letterSpacing: 3,
          color: dark ? "#c89bff" : PURPLE,
          textTransform: "uppercase",
        }}
      >
        {tune.type} {version ? `· version ${version}` : ""}
      </div>
      <div
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: tune.name.length > 28 ? 92 : 118,
          lineHeight: 1.02,
          fontWeight: 700,
          marginTop: 18,
        }}
      >
        {tune.name}
      </div>
      {fame != null && (
        <div style={{ marginTop: 28, fontSize: 32, fontWeight: 800, color: skin(dark).muted }}>
          Fame {Math.round(fame)} / 100 · {tune.settings?.length || 1} settings ·{" "}
          {tune.recordings_count ?? 0} recordings
        </div>
      )}
    </div>
    <div style={{ marginTop: 48 }}>
      <Domain dark={dark} size={30} />
    </div>
  </Frame>
);

// Just the tune, like the fullscreen view: title line + the notation, nothing else.
export const NotationCard = ({ w, h, abc, label, dark = false, fit = {} }) => (
  <Frame w={w} h={h} dark={dark} pad={48} id={`notation-${(fit.page || 0) + 1}`}>
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
      <span style={{ fontSize: 28, fontWeight: 900, color: skin(dark).muted }}>{label}</span>
      <span style={{ fontSize: 26, fontWeight: 900, color: PURPLE }}>foinn.org</span>
    </div>
    <div
      style={{
        marginTop: 18,
        flex: 1,
        minHeight: 0,
        background: "#fff",
        border: `3px solid ${skin(dark).border}`,
        borderRadius: 24,
        padding: 26,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <FitNotation abc={abc} {...fit} />
    </div>
  </Frame>
);

export const AbcCard = ({ w, h, abc, label = "The same tune in ABC", badge, dark = false, fit = {} }) => (
  <Frame w={w} h={h} dark={dark} id={`abc-${(fit.page || 0) + 1}`}>
    <CardHeader right={badge} dark={dark} />
    <div style={{ fontSize: 34, fontWeight: 900, marginTop: 26 }}>
      {label}
      {fit.pages > 1 ? ` · ${(fit.page || 0) + 1}/${fit.pages}` : ""}
    </div>
    <FitAbcText
      abc={abc}
      {...fit}
      style={{ marginTop: 20, background: INK, color: CREAM, borderRadius: 28, padding: 30 }}
    />
    <div style={{ marginTop: 22, fontSize: 26, fontWeight: 800, color: skin(dark).muted }}>
      Plain text you can edit — the notation follows instantly
    </div>
  </Frame>
);

const Field = ({ k, v, dark }) => (
  <div
    style={{
      border: `3px solid ${skin(dark).border}`,
      borderRadius: 22,
      padding: 20,
      background: skin(dark).panel,
    }}
  >
    <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: 2, color: PURPLE, textTransform: "uppercase" }}>
      {k}
    </div>
    <div style={{ fontSize: 28, fontWeight: 700, marginTop: 8, lineHeight: 1.25, color: skin(dark).ink }}>{v}</div>
  </div>
);

export const AnalysisCard = ({ w, h, tune, analysis, dark = false }) => (
  <Frame w={w} h={h} dark={dark} id="analysis">
    <CardHeader right="EXPERT ANALYSIS" dark={dark} />
    <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 64, fontWeight: 700, marginTop: 18 }}>
      {tune?.name}
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 20 }}>
      <Field k="Composer" v={analysis.composer} dark={dark} />
      <Field k="Date" v={analysis.date} dark={dark} />
      <Field k="Region" v={analysis.region || analysis.origin} dark={dark} />
      <Field k="First recording" v={analysis.first_recording} dark={dark} />
    </div>
    <p style={{ marginTop: 24, fontSize: 28, lineHeight: 1.45, fontWeight: 600, overflow: "hidden", flex: 1 }}>
      {(analysis.history || "").slice(0, 620)}
    </p>
    <div style={{ marginTop: 10, fontSize: 26, fontWeight: 800, color: skin(dark).muted }}>
      Already-studied tunes are free for everyone on foinn.org
    </div>
  </Frame>
);

// --------------------------------------------------------------- feature posts
export const FeatureCoverCard = ({ w, h, feature, dark = true }) => (
  <Frame w={w} h={h} dark={dark} id="feature-cover">
    <Wordmark badge="full" size={92} dark={dark} />
    <div style={{ marginTop: "auto" }}>
      <div
        style={{
          fontSize: 32,
          fontWeight: 900,
          letterSpacing: 4,
          color: dark ? "#c89bff" : PURPLE,
          textTransform: "uppercase",
        }}
      >
        {feature.tag}
      </div>
      <div
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: 110,
          lineHeight: 1.02,
          fontWeight: 700,
          marginTop: 16,
        }}
      >
        {feature.title}
      </div>
    </div>
    <div style={{ marginTop: 44 }}>
      <Domain dark={dark} size={30} />
    </div>
  </Frame>
);

const Bullets = ({ items, dark }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 28, margin: "auto 0" }}>
    {items.map((b, i) => (
      <div key={i} style={{ display: "flex", gap: 18, alignItems: "flex-start" }}>
        <span
          style={{
            width: 46,
            height: 46,
            borderRadius: 14,
            background: PURPLE,
            color: "#fff",
            fontWeight: 900,
            fontSize: 26,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {i + 1}
        </span>
        <span style={{ fontSize: 31, fontWeight: 700, lineHeight: 1.3, color: skin(dark).ink }}>{b}</span>
      </div>
    ))}
  </div>
);

export const FeatureSlideCard = ({ w, h, feature, slide, abc, analysis, index, dark = false, fit = {}, demo = {}, tune }) => (
  <Frame w={w} h={h} dark={dark} pad={56} id={`feature-${index}-${fit.page || 0}`}>
    <CardHeader right={feature.tag.toUpperCase()} dark={dark} />
    <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 56, fontWeight: 700, marginTop: 14 }}>
      {feature.title}
    </div>
    <div style={{ fontSize: 29, fontWeight: 800, color: skin(dark).muted, marginTop: 8 }}>{slide.caption}</div>

    <div style={{ marginTop: 18, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      {slide.kind === "notation" && (
        <NotationPanel abc={slide.transpose ? transposeAbc(abc, slide.transpose) : abc} dark={dark} fit={fit} />
      )}
      {slide.kind === "abc" && (
        <>
          {slide.badge && (
            <span
              style={{
                alignSelf: "flex-start",
                background: PURPLE,
                color: "#fff",
                borderRadius: 999,
                padding: "10px 24px",
                fontSize: 27,
                fontWeight: 900,
                marginBottom: 14,
              }}
            >
              {slide.badge}
            </span>
          )}
          <FitAbcText abc={abc} base={26} {...fit} style={{ background: INK, color: CREAM, borderRadius: 26, padding: 28 }} />
        </>
      )}
      {slide.kind === "export" && <ExportMock abc={abc} dark={dark} fit={fit} />}
      {slide.kind === "export-landscape" && <ExportMock abc={abc} dark={dark} landscape fit={fit} />}
      {slide.kind.startsWith("screen-") && (
        <ScreenSlide kind={slide.kind} dark={dark} zoom={(fit.zoom || 100) / 100} demo={demo} tune={tune} />
      )}
      {slide.kind === "chart" && <AbcChartMock dark={dark} />}
      {slide.kind === "fame" && <FameMock dark={dark} />}
      {slide.kind === "namesearch" && <NameSearchMock dark={dark} />}
      {slide.kind === "flags" && <FlagsMock dark={dark} />}
      {slide.kind === "pricing" && <PricingTableMock dark={dark} />}
      {slide.kind === "install" && <InstallMock dark={dark} />}
      {slide.kind === "player" && <PlayerMock abc={abc} dark={dark} fit={fit} />}
      {slide.kind === "faq" && <FaqMock dark={dark} items={slide.items || []} />}
      {slide.kind === "rarity" && <RarityMock dark={dark} />}
      {slide.kind === "about" && <AboutCtaMock dark={dark} />}
      {slide.kind === "analysis" && analysis && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Field k="Composer" v={analysis.composer} dark={dark} />
          <Field k="Date" v={analysis.date} dark={dark} />
          <Field k="Region" v={analysis.region || analysis.origin} dark={dark} />
          <Field k="First recording" v={analysis.first_recording} dark={dark} />
          <div
            style={{
              gridColumn: "1 / -1",
              fontSize: 27,
              fontWeight: 600,
              lineHeight: 1.4,
              color: skin(dark).ink,
            }}
          >
            {(analysis.history || "").slice(0, 330)}
          </div>
        </div>
      )}
      {slide.kind === "bullets" && <Bullets items={feature.bullets} dark={dark} />}
    </div>

    <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 16 }}>
      <Sparkles size={30} color={PURPLE} />
      <span style={{ fontSize: 27, fontWeight: 900 }}>foinn.org · free, no advertising</span>
    </div>
  </Frame>
);
